import React, { ReactElement, useEffect, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { MultiSelectField, matchesLabel } from '@ui/components/ui/multi-select';
import { Textarea } from '@ui/components/ui/textarea';
import DateTimeField from '@app/components/shared/reportModal/DateTimeField';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  selectItemsByValue,
} from '@ui/components/ui/select';
import {
  IncidentDraft,
  IncidentDraftField,
  PRIORITIES,
  Priority,
  SupportAgreement,
  draftErrors,
  formatTarget,
  priorityLabel,
} from './sources/incidents';

// The fields an incident is written with: what happened, at what priority, when it came in and when it was answered.
const IncidentFields = ({
  draft,
  agreement,
  products,
  productId,
  askTimestamps = true,
  onProductChange,
  onChange,
}: {
  draft: IncidentDraft;
  /** Absent on a product with no agreement, which has no targets to show against a priority. */
  agreement?: SupportAgreement | null;
  // Only where the incident does not already know its product. No agreement means nothing to judge it by.
  products?: { id: string; name: string }[];
  productId?: string;
  /** Off for the client's own report, where neither timestamp is theirs to answer. */
  askTimestamps?: boolean;
  onProductChange?: (productId: string) => void;
  onChange: (draft: IncidentDraft) => void;
}): ReactElement => {
  const { t } = useTranslation();
  const errors = draftErrors(draft);
  // Nothing is wrong with a field nobody has been to yet. A blank form is missing everything by
  // definition, and opening it painted red says the person did something wrong before they started.
  const [visited, setVisited] = useState<IncidentDraftField[]>([]);
  useEffect(() => setVisited([]), [draft.id]);
  const visit = (field: IncidentDraftField): void =>
    setVisited(current => (current.includes(field) ? current : [...current, field]));
  const shownError = (field: IncidentDraftField): string | undefined =>
    visited.includes(field) ? errors[field] : undefined;
  const target = agreement?.targets.find(entry => entry.priority === draft.priority);
  const showProducts = !!products && !!onProductChange;

  const set = (field: keyof IncidentDraft, value: string): void => onChange({ ...draft, [field]: value });

  const fieldError = (field: IncidentDraftField): ReactElement | null => {
    const error = shownError(field);
    return error ? <p className="mt-1 text-[11px] text-destructive">{t(`incidents.error.${error}`)}</p> : null;
  };

  return (
    <div className="flex flex-col gap-4">
      {showProducts && (
        <div className="flex flex-col gap-1.5">
          <MultiSelectField
            single
            clearable={false}
            filter={matchesLabel}
            id="incident-product"
            label={t('incidents.product')}
            options={products.map(product => ({ value: product.id, label: product.name }))}
            value={productId ? [productId] : []}
            onChange={next => next[0] && onProductChange(next[0])}
            emptyLabel={t('incidents.chooseProduct')}
            searchPlaceholder={t('searchProduct')}
            emptySearchLabel={t('noProductsFound')}
          />
          <p className="text-[11px] text-muted-foreground">{t('incidents.productHint')}</p>
        </div>
      )}

      {/* Two fields because they are read in two places: the name in every queue, the write-up on its own page. */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="incident-title">{t('incidents.issueColumn')}</Label>
        <Input
          id="incident-title"
          value={draft.title}
          aria-invalid={!!shownError('title')}
          placeholder={t('incidents.titlePlaceholder')}
          className={cx(shownError('title') && 'border-destructive')}
          onBlur={() => visit('title')}
          onChange={event => set('title', event.target.value)}
        />
        {fieldError('title') ?? <p className="text-[11px] text-muted-foreground">{t('incidents.titleHint')}</p>}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="incident-description">{t('incidents.description')}</Label>
        <Textarea
          id="incident-description"
          rows={3}
          value={draft.description}
          placeholder={t('incidents.descriptionPlaceholder')}
          onChange={event => set('description', event.target.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label>{t('incidents.priority')}</Label>
          <Select
            value={draft.priority || null}
            // Visited when the list closes, not on blur: opening the list is what takes the focus away.
            onOpenChange={open => !open && visit('priority')}
            onValueChange={next => {
              visit('priority');
              if (next) set('priority', next as Priority);
            }}
            items={selectItemsByValue(
              PRIORITIES.map(priority => ({
                value: priority,
                label: priorityLabel(priority, t(`incidents.priorityName.${priority}`)),
              })),
            )}
          >
            <SelectTrigger className="w-full" aria-invalid={!!shownError('priority')}>
              <SelectValue placeholder={t('incidents.choosePriority')} />
            </SelectTrigger>
            <SelectContent align="start" alignItemWithTrigger={false}>
              {PRIORITIES.map(priority => (
                <SelectItem key={priority} value={priority}>
                  {priorityLabel(priority, t(`incidents.priorityName.${priority}`))}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* What this priority commits us to, on the screen where the priority is chosen. It is the
              difference between the four options, and the reason to pick one over another. */}
          {fieldError('priority') ??
            (target ? (
              <p className="text-[11px] text-muted-foreground">
                {t('incidents.targetHint', {
                  response: formatTarget(target.responseHours),
                  resolution: formatTarget(target.resolutionHours),
                })}
              </p>
            ) : null)}
        </div>
      </div>

      {askTimestamps && (
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <DateTimeField
              id="incident-reported"
              label={t('incidents.reportedAt')}
              value={draft.reportedAt}
              onChange={next => {
                visit('reportedAt');
                set('reportedAt', next);
              }}
              invalid={!!shownError('reportedAt')}
            />
            {fieldError('reportedAt')}
          </div>

          <div className="flex flex-col gap-1.5">
            <DateTimeField
              id="incident-responded"
              label={t('incidents.respondedAt')}
              value={draft.respondedAt}
              onChange={next => {
                visit('respondedAt');
                set('respondedAt', next);
              }}
              invalid={!!shownError('respondedAt')}
            />
            {fieldError('respondedAt') ?? (
              <p className="text-[11px] text-muted-foreground">{t('incidents.blankMeansOpen')}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentFields;
